import {isNil} from 'ramda';

import {invalidateOnSuccess, metadaApi} from '@dms/api/base';

import {
  AccGetConfigurationAuthorizationOperationIdApiArg,
  AccGetConfigurationAuthorizationOperationIdApiResponse,
  AccDeleteConfigurationApiArgMetadaAccounting,
  AccDeleteConfigurationApiResponseMetadaAccounting,
  AccDeleteConfigurationPaymentImportModeApiArgMetadaAccounting,
  AccDeleteConfigurationPaymentImportModeApiResponseMetadaAccounting,
  AccGetAccountingContextCodeTableIdApiArg,
  AccGetAccountingContextCodeTableIdApiResponse,
  AccGetConfigurationExportAbraApiArgMetadaAccounting,
  AccGetConfigurationExportAbraApiResponseMetadaAccounting,
  AccGetConfigurationExportCarisApiArgMetadaAccounting,
  AccGetConfigurationExportCarisApiResponseMetadaAccounting,
  AccGetConfigurationInvoiceCostItemApiArgMetadaAccounting,
  AccGetConfigurationInvoiceCostItemApiResponseMetadaAccounting,
  AccGetConfigurationInvoiceCostItemPlusApiArg,
  AccGetConfigurationInvoiceCostItemPlusApiResponse,
  AccGetConfigurationOperationIdDefaultApiArg,
  AccGetConfigurationOperationIdDefaultApiResponse,
  AccGetConfigurationPaymentImportModeApiArgMetadaAccounting,
  AccGetConfigurationPaymentImportModeApiResponseMetadaAccounting,
  AccGetMatrix202and205ApiArg,
  AccGetMatrix202and205ApiResponse,
  AccItemCodeServiceGetValidCodesApiResponse,
  AccM2StrediskoGetCentresApiArgMetadaAccounting,
  AccM2StrediskoGetCentresApiResponseMetadaAccounting,
  AccPatchConfigurationAuthorizationOperationIdApiArg,
  AccPatchConfigurationAuthorizationOperationIdApiResponse,
  AccPatchConfigurationExportAbraApiArgMetadaAccounting,
  AccPatchConfigurationExportAbraApiResponseMetadaAccounting,
  AccPatchConfigurationExportCarisApiArgMetadaAccounting,
  AccPatchConfigurationExportCarisApiResponseMetadaAccounting,
  AccPatchConfigurationInvoiceCostItemApiArgMetadaAccounting,
  AccPatchConfigurationInvoiceCostItemApiResponseMetadaAccounting,
  AccPatchConfigurationInvoiceCostItemPlusApiArg,
  AccPatchConfigurationInvoiceCostItemPlusApiResponse,
  AccPatchConfigurationOperationIdDefaultApiArg,
  AccPatchConfigurationOperationIdDefaultApiResponse,
  AccPatchConfigurationPaymentImportModeApiArgMetadaAccounting,
  AccPatchConfigurationPaymentImportModeApiResponseMetadaAccounting,
  AccPatchDocumentExportDateSetNullApiArgMetadaAccounting,
  AccPatchDocumentExportDateSetNullApiResponseMetadaAccounting,
  AccPostAccountingContextCodeTableApiArg,
  AccPostAccountingContextCodeTableApiResponse,
  AccPostConfigurationAuthorizationOperationIdApiArg,
  AccPostConfigurationAuthorizationOperationIdApiResponse,
  AccPostConfigurationInvoiceCostItemApiArgMetadaAccounting,
  AccPostConfigurationInvoiceCostItemApiResponseMetadaAccounting,
  AccPostConfigurationPaymentImportModeApiArgMetadaAccounting,
  AccPostConfigurationPaymentImportModeApiResponseMetadaAccounting,
  AccPostMatrixConfigInitializeApiArgMetadaAccounting,
  AccPostMatrixConfigInitializeApiResponseMetadaAccounting,
  GetCategoryApiArgMetadaAccounting,
  GetCategoryApiResMetadaAccounting,
  GetOperationalItemsRequestMetadaAccounting,
  GetOperationalItemsResponseMetadaAccounting,
  GetStockMovementsRequestMetadaAccounting,
  GetStockMovementsResponseMetadaAccounting,
  MatricesConfigCleanupApiArgMetadaAccounting,
  MatricesConfigCleanupApiResMetadaAccounting,
  MoveAllMatricesFromCarisApiArgMetadaAccounting,
  MoveAllMatricesFromCarisApiResMetadaAccounting,
  MoveMatricesApiArgMetadaAccounting,
  MoveMatricesApiResMetadaAccounting,
  AccGetConfigurationAuthorizationsApiResponse,
} from './types';

export const metadaAccountingApi = metadaApi.injectEndpoints({
  endpoints: (build) => ({
    getStockMovementTypes: build.query<
      GetStockMovementsResponseMetadaAccounting[],
      GetStockMovementsRequestMetadaAccounting
    >({
      query: () => ({
        url: `accounting/v1/stock-movement-types?applicationId=Servis&movementDirection=1`,
        method: 'GET',
        invalidatesTags: ['stockMovements'],
      }),
    }),
    getOperationItems: build.query<
      GetOperationalItemsResponseMetadaAccounting[],
      GetOperationalItemsRequestMetadaAccounting
    >({
      query: (queryArg) => ({
        url: 'accounting/v1/operation-items',
        method: 'GET',
        params: queryArg,
        invalidatesTags: ['listOfItems'],
      }),
    }),
    getAccountingDictionary: build.query<
      GetCategoryApiResMetadaAccounting,
      GetCategoryApiArgMetadaAccounting
    >({
      query: ({category}) => ({
        url: `accounting/v1/dictionary/${category}`,
        method: 'GET',
      }),
      providesTags: ['accountingDictionary'],
    }),
    moveMatrices: build.mutation<
      MoveMatricesApiResMetadaAccounting,
      MoveMatricesApiArgMetadaAccounting
    >({
      query: ({category, ...body}) => ({
        url: `accounting/v1/${category}`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['accountingDictionary'],
    }),
    matricesConfigCleanup: build.mutation<
      MatricesConfigCleanupApiResMetadaAccounting,
      MatricesConfigCleanupApiArgMetadaAccounting
    >({
      query: (body) => ({
        url: `accounting/v1/matrices-config-db-clean-up`,
        method: 'POST',
        body,
      }),
    }),
    moveAllMatricesFromCaris: build.mutation<
      MoveAllMatricesFromCarisApiResMetadaAccounting,
      MoveAllMatricesFromCarisApiArgMetadaAccounting
    >({
      query: ({googleDocId, sheetName, ...params}) => ({
        url: `accounting/v1/convert-matrices-from-caris/${googleDocId}/${sheetName}`,
        method: 'POST',
        params,
      }),
      invalidatesTags: ['accountingDictionary'],
    }),
    postConfigurationPaymentImportMode: build.mutation<
      AccPostConfigurationPaymentImportModeApiResponseMetadaAccounting,
      AccPostConfigurationPaymentImportModeApiArgMetadaAccounting
    >({
      query: (queryArg) => ({
        url: `accounting/v1/configuration/payment-import-mode`,
        method: 'POST',
        body: queryArg.body,
      }),
    }),
    patchConfigurationPaymentImportMode: build.mutation<
      AccPatchConfigurationPaymentImportModeApiResponseMetadaAccounting,
      AccPatchConfigurationPaymentImportModeApiArgMetadaAccounting
    >({
      query: (queryArg) => ({
        url: `accounting/v1/configuration/payment-import-mode`,
        method: 'PATCH',
        body: queryArg.body,
        params: {branchId: queryArg.branchId},
      }),
    }),
    getConfigurationPaymentImportMode: build.query<
      AccGetConfigurationPaymentImportModeApiResponseMetadaAccounting,
      AccGetConfigurationPaymentImportModeApiArgMetadaAccounting
    >({
      query: (queryArg) => ({
        url: `accounting/v1/configuration/payment-import-mode/${queryArg.branchId}`,
      }),
      keepUnusedDataFor: 0,
    }),
    deleteConfigurationPaymentImportMode: build.mutation<
      AccDeleteConfigurationPaymentImportModeApiResponseMetadaAccounting,
      AccDeleteConfigurationPaymentImportModeApiArgMetadaAccounting
    >({
      query: (queryArg) => ({
        url: `accounting/v1/configuration/payment-import-mode/${queryArg.branchId}`,
        method: 'DELETE',
      }),
    }),
    PostConfigurationInvoiceCostItem: build.mutation<
      AccPostConfigurationInvoiceCostItemApiResponseMetadaAccounting,
      AccPostConfigurationInvoiceCostItemApiArgMetadaAccounting
    >({
      query: (queryArg) => ({
        url: `accounting/v1/configuration/invoice-cost-item/used-cars`,
        method: 'POST',
        body: queryArg,
      }),
    }),
    GetConfigurationInvoiceCostItem: build.query<
      AccGetConfigurationInvoiceCostItemApiResponseMetadaAccounting,
      AccGetConfigurationInvoiceCostItemApiArgMetadaAccounting
    >({
      query: (queryArg) => ({
        url: `accounting/v1/configuration/invoice-cost-item/${queryArg.id}`,
      }),
      keepUnusedDataFor: 0,
    }),
    PatchConfigurationInvoiceCostItem: build.mutation<
      AccPatchConfigurationInvoiceCostItemApiResponseMetadaAccounting,
      AccPatchConfigurationInvoiceCostItemApiArgMetadaAccounting
    >({
      query: (queryArg) => ({
        url: `accounting/v1/configuration/invoice-cost-item/${queryArg.id}`,
        method: 'PATCH',
        params: queryArg,
      }),
    }),
    DeleteConfiguration: build.mutation<
      AccDeleteConfigurationApiResponseMetadaAccounting,
      AccDeleteConfigurationApiArgMetadaAccounting
    >({
      query: (queryArg) => ({
        url: `accounting/v1/configuration/record/${queryArg.id}`,
        method: 'DELETE',
      }),
    }),
    accPostMatrixConfigInitialize: build.mutation<
      AccPostMatrixConfigInitializeApiResponseMetadaAccounting,
      AccPostMatrixConfigInitializeApiArgMetadaAccounting
    >({
      query: () => ({
        url: `accounting/v1/matrix-config-initialization`,
        method: 'POST',
      }),
    }),
    accGetConfigurationExportCaris: build.query<
      AccGetConfigurationExportCarisApiResponseMetadaAccounting,
      AccGetConfigurationExportCarisApiArgMetadaAccounting
    >({
      query: () => ({
        url: `accounting/v1/configuration/caris-export`,
      }),
      providesTags: ['carisExport'],
    }),
    accPatchConfigurationExportCaris: build.mutation<
      AccPatchConfigurationExportCarisApiResponseMetadaAccounting,
      AccPatchConfigurationExportCarisApiArgMetadaAccounting
    >({
      query: (body) => ({
        url: `accounting/v1/configuration/caris-export`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['carisExport'],
    }),
    accGetConfigurationExportAbra: build.query<
      AccGetConfigurationExportAbraApiResponseMetadaAccounting,
      AccGetConfigurationExportAbraApiArgMetadaAccounting
    >({
      query: () => ({
        url: `accounting/v1/configuration/abra`,
      }),
      providesTags: ['abraConfiguration'],
    }),
    accPatchConfigurationExportAbra: build.mutation<
      AccPatchConfigurationExportAbraApiResponseMetadaAccounting,
      AccPatchConfigurationExportAbraApiArgMetadaAccounting
    >({
      query: ({body, disable}) => ({
        url: `accounting/v1/configuration/abra`,
        method: 'PATCH',
        body,
        params: isNil(disable) ? undefined : {disable},
      }),
      invalidatesTags: invalidateOnSuccess(['abraConfiguration']),
    }),
    accGetConfigurationInvoiceCostItemPlus: build.query<
      AccGetConfigurationInvoiceCostItemPlusApiResponse,
      AccGetConfigurationInvoiceCostItemPlusApiArg
    >({
      query: () => ({
        url: `accounting/v1/configuration/invoice-cost-item/subcontract`,
      }),
      providesTags: ['invoiceCostItemSubcontracts'],
    }),
    accPatchConfigurationInvoiceCostItemPlus: build.mutation<
      AccPatchConfigurationInvoiceCostItemPlusApiResponse,
      AccPatchConfigurationInvoiceCostItemPlusApiArg
    >({
      query: (queryArg) => ({
        url: `accounting/v1/configuration/invoice-cost-item/subcontract`,
        method: 'PATCH',
        params: {include: queryArg.include},
      }),
      invalidatesTags: ['invoiceCostItemSubcontracts'],
    }),
    GetConfigurationOperationIdDefault: build.query<
      AccGetConfigurationOperationIdDefaultApiResponse,
      AccGetConfigurationOperationIdDefaultApiArg
    >({
      query: (queryArg) => ({
        url: `accounting/v1/configuration/operation-id-default/${queryArg.id}`,
      }),
      providesTags: ['operationDefaults'],
    }),
    PatchConfigurationOperationIdDefault: build.mutation<
      AccPatchConfigurationOperationIdDefaultApiResponse,
      AccPatchConfigurationOperationIdDefaultApiArg
    >({
      query: (queryArg) => ({
        url: `accounting/v1/configuration/operation-id-default/${queryArg.id}`,
        method: 'PATCH',
        params: {operationId: queryArg.operationId},
      }),
      invalidatesTags: ['operationDefaults'],
    }),
    getConfigurationAuthorizationOperationId: build.query<
      AccGetConfigurationAuthorizationOperationIdApiResponse,
      AccGetConfigurationAuthorizationOperationIdApiArg
    >({
      query: (queryArg) => ({
        url: `accounting/v1/configuration/authorization-operation-id/${queryArg.id}`,
      }),
    }),
    postConfigurationAuthorizationOperationId: build.mutation<
      AccPostConfigurationAuthorizationOperationIdApiResponse,
      AccPostConfigurationAuthorizationOperationIdApiArg
    >({
      query: (queryArg) => ({
        url: `accounting/v1/configuration/authorization-operation-id`,
        method: 'POST',
        body: queryArg.body,
      }),
      invalidatesTags: ['authorizationOperations'],
    }),
    patchConfigurationAuthorizationOperationId: build.mutation<
      AccPatchConfigurationAuthorizationOperationIdApiResponse,
      AccPatchConfigurationAuthorizationOperationIdApiArg
    >({
      query: (queryArg) => ({
        url: `accounting/v1/configuration/authorization-operation-id/${queryArg.id}`,
        method: 'PATCH',
        params: {authorizationId: queryArg.authorizationId, operationId: queryArg.operationId},
      }),
      invalidatesTags: ['authorizationOperations'],
    }),
    getConfigurationAuthorizations: build.query<AccGetConfigurationAuthorizationsApiResponse, void>(
      {
        query: () => ({
          url: `accounting/v1/authorizations`,
        }),
      }
    ),
    accM2StrediskoGetCentres: build.query<
      AccM2StrediskoGetCentresApiResponseMetadaAccounting,
      AccM2StrediskoGetCentresApiArgMetadaAccounting | void
    >({
      query: (queryArg) => ({
        url: `accounting/v1/m2-centres`,
        params: {accountingDate: queryArg?.accountingDate, code: queryArg?.code},
      }),
    }),
    accPatchDocumentExportDateSetNull: build.mutation<
      AccPatchDocumentExportDateSetNullApiResponseMetadaAccounting,
      AccPatchDocumentExportDateSetNullApiArgMetadaAccounting
    >({
      query: (queryArg) => ({
        url: `accounting/v1/document-export-flag/${queryArg.type}/${queryArg.id}`,
        method: 'PATCH',
      }),
    }),
    accItemCodeServiceGetValidCodes: build.query<
      AccItemCodeServiceGetValidCodesApiResponse,
      undefined
    >({
      query: () => ({
        url: `accounting/v1/item-codes`,
        params: {
          application: 'used-cars',
        },
      }),
    }),
    accGetMatrix202and205: build.query<
      AccGetMatrix202and205ApiResponse,
      AccGetMatrix202and205ApiArg
    >({
      query: (queryArg) => ({
        url: `accounting/v1/matrix202and205/${queryArg.orderVariantPart}`,
        method: 'GET',
      }),
    }),
    getAccountingContextCodeTableId: build.query<
      AccGetAccountingContextCodeTableIdApiResponse,
      AccGetAccountingContextCodeTableIdApiArg
    >({
      query: (queryArg) => ({
        url: `accounting/v1/accounting-context-code-table/${queryArg.referenceId}`,
      }),
      providesTags: (_result, _error, queryArg) => [
        {type: 'accountingContextCodeTable', id: queryArg.referenceId},
      ],
    }),
    postAccountingContextCodeTable: build.mutation<
      AccPostAccountingContextCodeTableApiResponse,
      AccPostAccountingContextCodeTableApiArg
    >({
      query: (queryArg) => ({
        url: `accounting/v1/accounting-context-code-table/${queryArg.attributeName}`,
        method: 'POST',
        body: queryArg.body,
      }),
      invalidatesTags: ['accountingContextCodeTable'],
    }),
  }),
});

export const {
  useAccPatchDocumentExportDateSetNullMutation,
  useAccGetConfigurationExportCarisQuery,
  useAccPatchConfigurationExportCarisMutation,
  useAccGetConfigurationExportAbraQuery,
  useAccPatchConfigurationExportAbraMutation,
  useMatricesConfigCleanupMutation,
  useAccPostMatrixConfigInitializeMutation,
  usePatchConfigurationInvoiceCostItemMutation,
  usePostConfigurationInvoiceCostItemMutation,
  useGetConfigurationInvoiceCostItemQuery,
  useAccGetConfigurationInvoiceCostItemPlusQuery,
  useAccPatchConfigurationInvoiceCostItemPlusMutation,
  useGetConfigurationOperationIdDefaultQuery,
  usePatchConfigurationOperationIdDefaultMutation,
  useGetConfigurationAuthorizationOperationIdQuery,
  usePostConfigurationAuthorizationOperationIdMutation,
  usePatchConfigurationAuthorizationOperationIdMutation,
  useGetConfigurationAuthorizationsQuery,
  useDeleteConfigurationMutation,
  useDeleteConfigurationPaymentImportModeMutation,
  usePatchConfigurationPaymentImportModeMutation,
  usePostConfigurationPaymentImportModeMutation,
  useGetConfigurationPaymentImportModeQuery,
  useGetStockMovementTypesQuery,
  useGetOperationItemsQuery,
  useGetAccountingDictionaryQuery,
  useMoveMatricesMutation,
  useMoveAllMatricesFromCarisMutation,
  useAccM2StrediskoGetCentresQuery,
  useAccItemCodeServiceGetValidCodesQuery,
  useAccGetMatrix202and205Query,
  useGetAccountingContextCodeTableIdQuery,
  usePostAccountingContextCodeTableMutation,
} = metadaAccountingApi;
